// PreviewCompany.jsx - Fixed version
import React, { useEffect, useState } from 'react';
import { usePreviewContext } from '../../../context/PreviewContext';
import { useLazyQuery, useQuery } from '@apollo/client';
import { companyFromBookletQuery } from '../../../queries/booklet';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import BookletCompany from '../../vara-haften/BookletCompany';
import CompanyModalCoupon from '../../vara-haften/CompanyModalCoupon';
import { getCompanyTypesQuery } from '../../../queries/company';
import CompanyHeader from '../../vara-haften/CompanyHeader';

function PreviewCompany({ togglePreview }) {
  const {
    isPreviewOpen,
    bookletCompanyPreview,
    couponsToPreview,
    modalCompanyPreview,
    previewType,
    setPreviewType,
    setIsPreviewOpen,
    resetPreview,
  } = usePreviewContext();

  const { data: companyTypesData } = useQuery(getCompanyTypesQuery);
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [getCompanyPreview] = useLazyQuery(companyFromBookletQuery, {
    onCompleted: (data) => {
      setIsLoading(false);
      if (data?.companyFromBooklet) {
        setCompany(data.companyFromBooklet);
        if (previewType !== 'company') {
          setPreviewType('company');
        }
        setIsPreviewOpen(true);
      }
    },
    onError: (error) => {
      setIsLoading(false);
      console.error('Error fetching company:', error);
    },
    fetchPolicy: 'network-only',
  });

  // Clear company when preview source changes
  useEffect(() => {
    setCompany(null);
    setIsLoading(true);

    if (bookletCompanyPreview?.bookletId && bookletCompanyPreview?.companyId) {
      getCompanyPreview({
        variables: {
          bookletId: bookletCompanyPreview.bookletId,
          companyId: bookletCompanyPreview.companyId
        }
      });
    } else if (modalCompanyPreview?._id) {
      setCompany(modalCompanyPreview);
      setIsLoading(false);
      if (previewType !== 'company') {
        setPreviewType('company');
      }
      setIsPreviewOpen(true);
    } else {
      setIsLoading(false);
    }
  }, [bookletCompanyPreview, modalCompanyPreview, getCompanyPreview, setPreviewType, setIsPreviewOpen, previewType]);

  // Transform coupons to the format expected by CompanyModalCoupon
  const transformCoupons = (coupons) => {
    if (!coupons || coupons.length === 0) return [];

    return coupons.map(couponWithState => {
      // Handle both formats (with .data or direct)
      const couponData = couponWithState.data || couponWithState;
      return {
        _id: couponWithState.id || couponWithState._id,
        discount: couponData.discount || '',
        smallTitle: couponData.smallTitle || '',
        description: couponData.description || '',
        terms: couponData.terms || '',
        imageURL: couponData.imageURL || null,
        code: couponData.code || null,
        codeType: couponData.codeType || null,
        position: couponData.position || 0,
        availableByUser: null,
        assessment: couponData.assessment || null,
      };
    });
  };

  // Determine which coupons to show - prioritize passed coupons over company coupons
  const couponsToDisplay = (() => {
    if (couponsToPreview && couponsToPreview.length > 0) {
      return transformCoupons(couponsToPreview);
    }
    if (company?.calculatedCoupons && company.calculatedCoupons.length > 0) {
      return company.calculatedCoupons;
    }
    if (company?.coupons?.data && company.coupons.data.length > 0) {
      return company.coupons.data;
    }
    return [];
  })();

  // Determine which company to show
  const displayCompany = company || modalCompanyPreview;

  // Don't render if loading
  if (isLoading) {
    return (
      <div className='company-preview'>
        <h3>Laddar preview...</h3>
        <button className='toggler' onClick={togglePreview}>
          {isPreviewOpen ? <BsChevronRight size={20} /> : <BsChevronLeft size={20} />}
        </button>
      </div>
    );
  }

  return (
    <div className='company-preview'>
      {!displayCompany && couponsToDisplay.length === 0 ? (
        <h3>Företaget kunde inte hittas i häftet.</h3>
      ) : (
        <>
          {/* Booklet preview header (from companies table) */}
          {bookletCompanyPreview && displayCompany && (
            <>
              <h3>Såhär ser ert företag ut i häftet!</h3>
              <div className="company-header">
                <BookletCompany
                  company={displayCompany}
                  companyType={companyTypesData?.types?.[displayCompany?.typeId]}
                />
              </div>
            </>
          )}

          {/* Always show the coupon view if we have company or coupons */}
          {(displayCompany || couponsToDisplay.length > 0) && (
            <>
              <h3>Såhär ser det ut när man klickat in på ert företag</h3>
              <div className="relative company-preview-coupons">
                {displayCompany && (
                  <CompanyHeader
                    company={displayCompany}
                    companyType={companyTypesData?.types?.[displayCompany?.typeId]}
                  />
                )}
                {couponsToDisplay.map(coupon => (
                  <CompanyModalCoupon key={coupon._id} coupon={coupon} />
                ))}
              </div>
            </>
          )}
        </>
      )}

      <button className='toggler' onClick={togglePreview}>
        {isPreviewOpen ? <BsChevronRight size={20} /> : <BsChevronLeft size={20} />}
      </button>
    </div>
  );
}

export default PreviewCompany;
